/**
 * Définition ou mise à jour d’un objectif CA mensuel (API ca_objectif_mensuels).
 * Panneau latéral droit — même ergonomie que le formulaire Projet / autres overlays.
 */

import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { extractIdRef, fmtEuroCommercialDash } from '../utils/commercialDashboardCa';

/** @typedef {{ id:number, moisRef:string }} CaObjApiRow */

/** Mois calendaires (valeur API = 01…12) */
const MOIS_OBJECTIF_OPTIONS = [
    { value: '01', label: 'Janvier' },
    { value: '02', label: 'Février' },
    { value: '03', label: 'Mars' },
    { value: '04', label: 'Avril' },
    { value: '05', label: 'Mai' },
    { value: '06', label: 'Juin' },
    { value: '07', label: 'Juillet' },
    { value: '08', label: 'Août' },
    { value: '09', label: 'Septembre' },
    { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Décembre' },
];

function ymValid(ym) {
    return typeof ym === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(ym.trim());
}

function formatMoisRefFr(ym) {
    if (!ymValid(ym)) return ym || '—';
    const [yy, mm] = ym.trim().split('-');
    const ent = MOIS_OBJECTIF_OPTIONS.find((o) => o.value === mm);
    return ent ? `${ent.label} ${yy}` : ym;
}

function defaultYearRange() {
    const y = new Date().getFullYear();
    const arr = [];
    for (let a = y - 3; a <= y + 5; a += 1) arr.push(a);
    return arr;
}

/** @type {CaObjApiRow[]} */
const EMPTY_ROWS = [];

const CaObjectifMensuelPanel = ({
    apiUrl,
    open,
    onClose,
    entityId,
    commercialId,
    commercialLabel,
    /** @type CaObjApiRow[] */
    allRowsForUpsertSearch = EMPTY_ROWS,
    defaultMoisRef,
    onSaved,
}) => {
    const [montant, setMontant] = useState('');
    /** YYYY-MM */
    const [moisChoice, setMoisChoice] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState('');

    React.useEffect(() => {
        if (!open || !commercialId || !entityId) return;
        const d = typeof defaultMoisRef === 'string' ? defaultMoisRef.trim() : '';
        if (ymValid(d)) {
            setMoisChoice(d);
        } else {
            const n = new Date();
            setMoisChoice(`${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`);
        }
        setMontant('');
        setError('');
    }, [open, commercialId, entityId, defaultMoisRef]);

    const anneesObjectif = useMemo(() => {
        const base = defaultYearRange();
        if (ymValid(moisChoice)) {
            const y = Number.parseInt(moisChoice.slice(0, 4), 10);
            if (Number.isFinite(y) && !base.includes(y)) {
                return [...base, y].sort((a, b) => a - b);
            }
        }
        return base;
    }, [moisChoice]);

    const moisCourantDigits = () => {
        const n = new Date();
        return {
            year: String(n.getFullYear()),
            month: String(n.getMonth() + 1).padStart(2, '0'),
        };
    };

    const partieAnnee = ymValid(moisChoice) ? moisChoice.slice(0, 4) : moisCourantDigits().year;
    const partieMois = ymValid(moisChoice) ? moisChoice.slice(5, 7) : moisCourantDigits().month;

    const appliquerMoisAnnee = (yStr, mStr) => {
        setMoisChoice(`${yStr}-${mStr}`);
    };

    const existingForMonth = useMemo(() => allRowsForUpsertSearch.find(
        (r) => extractIdRef(r?.commercial) === commercialId && r.moisRef === moisChoice,
    ), [allRowsForUpsertSearch, commercialId, moisChoice]);

    const handleDeleteObjectif = async () => {
        const id = existingForMonth?.id;
        if (id == null) return;
        const lib = formatMoisRefFr(moisChoice);
        const prev = fmtEuroCommercialDash(existingForMonth.montantObjectif);
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Supprimer l’objectif ${lib} (${prev}) pour ce commercial ? Vous pourrez en saisir un autre ensuite pour le même mois.`)) return;
        setError('');
        setDeleting(true);
        try {
            await axios.delete(`${apiUrl}/ca_objectif_mensuels/${id}`, {
                withCredentials: true,
                headers: { Accept: 'application/ld+json' },
            });
            setMontant('');
            onSaved?.();
        } catch (err) {
            const msg = err?.response?.data?.['hydra:description'] || err?.response?.data?.detail || err?.message || 'Erreur lors de la suppression';
            setError(typeof msg === 'string' ? msg : 'Erreur lors de la suppression');
        } finally {
            setDeleting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!commercialId || !entityId) return;
        setError('');
        const moisNorm = typeof moisChoice === 'string' ? moisChoice.trim() : '';
        const mEuro = montant.trim().replace(/\s+/g, '').replace(',', '.');
        const amt = parseFloat(mEuro);
        if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(moisNorm)) {
            setError('Choisissez un mois valide.');
            return;
        }
        if (!Number.isFinite(amt) || amt < 0) {
            setError('Montant invalide.');
            return;
        }
        setSaving(true);
        try {
            const body = {
                commercial: `/api/users/${commercialId}`,
                entite: `/api/entities/${entityId}`,
                moisRef: moisNorm,
                montantObjectif: String(amt),
            };
            if (existingForMonth?.id) {
                await axios.patch(`${apiUrl}/ca_objectif_mensuels/${existingForMonth.id}`, {
                    montantObjectif: String(amt),
                }, { withCredentials: true, headers: { Accept: 'application/ld+json', 'Content-Type': 'application/merge-patch+json' } });
            } else {
                await axios.post(`${apiUrl}/ca_objectif_mensuels`, body, { withCredentials: true, headers: { Accept: 'application/ld+json', 'Content-Type': 'application/ld+json' } });
            }
            onSaved?.();
            onClose?.();
        } catch (err) {
            const msg = err?.response?.data?.['hydra:description'] || err?.response?.data?.detail || err?.message || 'Erreur enregistrement';
            setError(typeof msg === 'string' ? msg : 'Erreur enregistrement');
        } finally {
            setSaving(false);
        }
    };

    if (!open || !commercialId || !entityId) return null;

    return (
        <div
            className="ed-ca-slide-overlay"
            role="presentation"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="ed-ca-slide-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ed-ca-slide-title"
                onMouseDown={(ev) => ev.stopPropagation()}
            >
                <div className="ed-ca-slide-panel-hd">
                    <div>
                        <h2 id="ed-ca-slide-title" className="ed-ca-slide-title">
                            Objectif CA — {commercialLabel}
                        </h2>
                        <p className="ed-ca-slide-sub">
                            Entité #{entityId} — un objectif par mois et par commercial.
                            Le CA du mois dans le tableau regroupe déjà tous les dossiers signés rattachés à ce mois (montage salon ou création du projet), même si vous créez ou modifiez l’objectif en cours de mois.
                        </p>
                    </div>
                    <button type="button" className="ed-ca-slide-close" onClick={onClose} aria-label="Fermer">
                        <X size={20} strokeWidth={2} />
                    </button>
                </div>
                <div className="ed-ca-slide-scroll">
                    <form className="ed-ca-slide-form" onSubmit={handleSubmit}>
                        <div className="ed-ca-slide-field ed-ca-slide-field--period">
                            <span className="ed-ca-slide-field-hd">Période de l&apos;objectif</span>
                            <div
                                className="ed-ca-slide-mois-row"
                                role="group"
                                aria-label="Mois et année de l&apos;objectif"
                            >
                                <div className="ed-ca-slide-select-wrap">
                                    <label htmlFor="ed-ca-obj-mois" className="ed-ca-slide-sr-only">Mois</label>
                                    <select
                                        id="ed-ca-obj-mois"
                                        className="ed-ca-slide-select"
                                        value={partieMois}
                                        onChange={(e) => appliquerMoisAnnee(partieAnnee, e.target.value)}
                                    >
                                        {MOIS_OBJECTIF_OPTIONS.map((m) => (
                                            <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="ed-ca-slide-select-wrap ed-ca-slide-select-wrap--year">
                                    <label htmlFor="ed-ca-obj-annee" className="ed-ca-slide-sr-only">Année</label>
                                    <select
                                        id="ed-ca-obj-annee"
                                        className="ed-ca-slide-select"
                                        value={partieAnnee}
                                        onChange={(e) => appliquerMoisAnnee(e.target.value, partieMois)}
                                    >
                                        {anneesObjectif.map((a) => (
                                            <option key={a} value={String(a)}>{a}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <label className="ed-ca-slide-field">
                            <span>Montant objectif (€ HT)</span>
                            <input
                                type="text"
                                inputMode="decimal"
                                placeholder="ex. 45000"
                                value={montant}
                                onChange={(ev) => setMontant(ev.target.value)}
                                required
                            />
                        </label>
                        {existingForMonth && (
                            <p className="ed-ca-slide-hint">
                                Mise à jour de l’objectif existant pour {formatMoisRefFr(moisChoice)}&nbsp;: {fmtEuroCommercialDash(existingForMonth.montantObjectif)}.
                            </p>
                        )}
                        {error && <p className="ed-ca-slide-error">{error}</p>}
                        <div className="ed-ca-slide-actions">
                            <div className="ed-ca-slide-actions-left">
                                {existingForMonth?.id != null && (
                                    <button
                                        type="button"
                                        className="ed-ca-slide-btn ed-ca-slide-btn--dangerGhost"
                                        disabled={saving || deleting}
                                        onClick={handleDeleteObjectif}
                                    >
                                        {deleting ? 'Suppression…' : 'Supprimer cet objectif'}
                                    </button>
                                )}
                            </div>
                            <div className="ed-ca-slide-actions-right">
                                <button type="button" className="ed-ca-slide-btn ed-ca-slide-btn--ghost" onClick={onClose}>
                                    Annuler
                                </button>
                                <button type="submit" className="ed-ca-slide-btn ed-ca-slide-btn--primary" disabled={saving || deleting}>
                                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CaObjectifMensuelPanel;
